import { Prescription, License } from '../types/data';

export const getCountDocument = (arr: Prescription[] | License[], type: string): number => {
  return arr.filter((prescription) => prescription.status.name === type).length;
};

export const naturalSort = (a: string | number | null | undefined, b: string | number | null | undefined): number => {
  const aStr = String(a ?? '');
  const bStr = String(b ?? '');

  const aParts = aStr.match(/(\d+|\D+)/g) || [];
  const bParts = bStr.match(/(\d+|\D+)/g) || [];

  const maxLength = Math.max(aParts.length, bParts.length);

  for (let i = 0; i < maxLength; i++) {
    const aPart = aParts[i] || '';
    const bPart = bParts[i] || '';

    const aNum = parseInt(aPart, 10);
    const bNum = parseInt(bPart, 10);

    if (!isNaN(aNum) && !isNaN(bNum)) {
      if (aNum !== bNum) {
        return aNum - bNum;
      }
    } else {
      const comparison = aPart.localeCompare(bPart, 'ru', { sensitivity: 'base' });
      if (comparison !== 0) {
        return comparison;
      }
    }
  }

  return 0;
};

export const calculateActionStatus = (actions: any[]): string => {
  if (!actions || actions.length === 0) {
    return 'Нет действий';
  }

  const getStatus = (action: any) => action.Status?.ID || action.Status?.$1q_1;
  const statuses = actions.map((action: any) => getStatus(action));

  // ID статусов из mytaskactionstatuses:
  // 227 = Не начато
  // 228 = В процессе
  // 229 = Просрочено
  // 230 = Отменено
  // 231 = Закрыто

  //! отменённые (230)
  const nonCancelledStatuses = statuses.filter((status: any) => status !== 230);

  if (nonCancelledStatuses.length === 0) {
    return 'Нет действий';
  }

  //! Если хотя бы одно действие просрочено
  if (nonCancelledStatuses.some((status: any) => status === 229)) {
    return 'Просрочено';
  }
  //! Если все не-отменённые действия закрыты
  else if (nonCancelledStatuses.every((status: any) => status === 231)) {
    return 'Закрыто';
  }
  //! Если все не-отменённые действия не начаты
  else if (nonCancelledStatuses.every((status: any) => status === 227)) {
    return 'Не начато';
  }
  //! В остальных случаях - в процессе
  else {
    return 'В процессе';
  }
};

const getViolationId = (value: string): number | undefined => {
  const regex = /#Предписание_(\d+)/;
  const match = value.match(regex);

  if (match) return Number(match[1]);
};

const getActionId = (value: string): number | undefined => {
  const regex = /#Лицензии_(\d+)/;
  const match = value.match(regex);

  if (match) return Number(match[1]);
};

export const generatePrescriptions = (prescriptions: any, violations: any, actions: any, users?: any, useDbStatus: boolean = true) => {
  return prescriptions
    .map((prescription: any) => {
      if (prescription?.PrescriptionNumber === '-') {
        return null;
      }
      const relatedViolations = violations?.filter((violation: any) => violation?.ParentFormId === prescription.id) || [];

      const formattedViolations = relatedViolations.map((violation: any) => {
        const relatedActions = actions.filter((action: any) => getViolationId(action.ImportantHashTag) === violation.id);

        return {
          id: violation.id,
          documentId: violation.documentId,
          text: violation.ViolationText,
          actions: relatedActions,
        };
      });

      const allActions = formattedViolations.flatMap((v) => v.actions);

      // console.log('allActions', allActions);

      const getStatus = (action: any) => action.Status?.ID || action.Status?.$1q_1;
      const actionStatus = calculateActionStatus(allActions);

      let prescriptionStatus;

      //! Если useDbStatus = true (для таблицы) - берём статус из БД
      if (useDbStatus && prescription.PrescriptionStatus) {
        prescriptionStatus = {
          id: prescription.PrescriptionStatus.id,
          name: prescription.PrescriptionStatus.Title || prescription.PrescriptionStatus.name,
        };
      } else {
        const deadline = new Date(prescription.DeadlineExecutionOrder);
        const now = new Date();

        const nextDay = new Date(now);
        nextDay.setDate(nextDay.getDate() - 1);

        if (allActions.length > 0 && allActions.every((action: any) => getStatus(action) === 231 || getStatus(action) === 230)) {
          prescriptionStatus = {
            id: 2, // Выполнено (из БД)
            name: 'Выполнено',
          };
        } else if (deadline <= nextDay) {
          prescriptionStatus = {
            id: 3, // Просрочено (из БД)
            name: 'Просрочено',
          };
        } else {
          prescriptionStatus = {
            id: 1, // В процессе (из БД)
            name: 'В процессе',
          };
        }
      }

      const lastClosedAction = allActions
        .filter((action: any) => getStatus(action) === 231 || getStatus(action) === 230)
        .sort((a: any, b: any) => new Date(b.Modified).getTime() - new Date(a.Modified).getTime())[0];

      return {
        docId: prescription.documentId,
        id: prescription.id,
        prescriptionNumber: prescription.PrescriptionNumber,
        status: prescriptionStatus,
        responsible: {
          id: prescription.Responsible?.id,
          email: prescription.ResponsibleEmail || prescription.Responsible?.Email,
          title: prescription.ResponsibleEmail
            ? users[prescription.ResponsibleEmail]?.displayName || prescription.Responsible?.Name
            : users[prescription.Responsible?.Email]?.displayName || prescription.Responsible?.Name,
          avatar: prescription.ResponsibleEmail ? users[prescription.ResponsibleEmail]?.avatar : users[prescription.Responsible?.Email]?.avatar,
          shortName: prescription.ResponsibleEmail ? users[prescription.ResponsibleEmail]?.shortName : users[prescription.Responsible?.Email]?.shortName,
        },
        direction: prescription.Direction
          ? {
              id: prescription.Direction.id,
              name: prescription.Direction.Title,
            }
          : null,
        location: prescription.GOSLocation
          ? {
              id: prescription.GOSLocation.id,
              name: prescription.GOSLocation.Title,
            }
          : null,
        dateVerification: {
          startDate: new Date(prescription.DateVerification),
          endDate: new Date(prescription.DateVerification),
        },
        deadlineExecutionOrder: {
          startDate: new Date(prescription.DeadlineExecutionOrder),
          endDate: new Date(prescription.DeadlineExecutionOrder),
        },
        actualClosingDatePrescription: lastClosedAction
          ? {
              startDate: new Date(lastClosedAction.Modified),
              endDate: new Date(lastClosedAction.Modified),
            }
          : null,
        agencyInspection: prescription.AgencyInspection || '',
        scopeVerification: prescription.ScopeVerification || '',
        typeVerification: prescription.TypeVerification || '',
        fieldActivity: prescription.FieldActivity || '',
        departmentsAudit: prescription.DepartmentsAudit || '',
        nameRegulatoryDocument: prescription.NameRegulatoryDocument || '',
        interestedParties: (prescription.InterestedPartiesEmails || prescription.InterestedParties || []).map((emailOrUser: any) => {
          const email = typeof emailOrUser === 'string' ? emailOrUser : (emailOrUser.Email || emailOrUser.Name);
          return {
            id: typeof emailOrUser === 'string' ? null : emailOrUser.id,
            title: users[email]?.displayName || email,
            email: email,
            avatar: users[email]?.avatar,
            shortName: users[email]?.shortName,
          };
        }),
        identifiedViolations: [],
        actionStatus,
        amountFine: prescription.AmountFine || '',
        fundsEliminationComments: prescription.FundsEliminationComments || '',
        comment: prescription.Comments || '',
        periodicNotificationUpdateDate:
          prescription.PeriodicNotificationUpdateDate === null || prescription.PeriodicNotificationUpdateDate === 'нет'
            ? {
                id: 1,
                name: 'Нет',
              }
            : {
                id: 2,
                name: 'Да',
              },
        frequencyRepeatedNotifications: prescription.FrequencyRepeatedNotifications === null ? 0 : prescription.FrequencyRepeatedNotifications,
        dateUpdate: prescription.DateUpdate
          ? {
              startDate: new Date(prescription.DateUpdate),
              endDate: new Date(prescription.DateUpdate),
            }
          : null,
        violations: formattedViolations,
      };
    })
    .filter((prescription: any) => prescription !== null);
};

export const generateLicenses = (licenses: any, actions: any, users?: any) => {
  return licenses
  .map((license: any) => {
    if (license.DocumentNumber === '-') {
      return null;
    }
    // console.log('licenses',licenses)
      const relatedActions = actions.filter((action: any) => getActionId(action.ImportantHashTag) === license.id);

      const formattedActions = relatedActions.map((action: any) => ({
        id: action.ID,
        title: action.Title,
        description: action.Description,
        statusAction: action.Status,
        created: action.Created,
        meeting: action.Meeting,
        dateForDone: action.DateForDone,
        responsibleAction: action.Responsible,
      }));

      const actionStatus = calculateActionStatus(relatedActions);

      const licensesStatusesMap: Record<string, string> = {
        1: 'В процессе',
        2: 'Действующий',
        3: 'Просрочен',
        4: 'В архиве',
      };

      let licenseStatuses = {
        id: license.LicenseStatus.id,
        name: licensesStatusesMap[license.LicenseStatus.id],
      };

      if (license.IsArchived === true) {
        licenseStatuses = {
          id: null,
          name: 'В архиве',
        };
      } else {
        const startDate = license.StartDateDocument ? new Date(license.StartDateDocument) : null;
        const endDate = license.ExpirationDateDocument ? new Date(license.ExpirationDateDocument) : null;
        const currentDate = new Date();

        const nextDay = new Date(currentDate);
        nextDay.setDate(nextDay.getDate() - 1);

        if (startDate && startDate <= currentDate && (!endDate || endDate > nextDay)) {
          licenseStatuses = {
            id: null,
            name: 'Действующий',
          };
        } else if (endDate && endDate <= nextDay) {
          licenseStatuses = {
            id: null,
            name: 'Просрочен',
          };
        }
      }

      return {
        docId: license.documentId,
        id: license.id,
        location: license.GOSLocation
          ? {
              id: license.GOSLocation.id,
              name: license.GOSLocation.Title,
            }
          : null,
        status: licenseStatuses,
        typeActivity: license.TypeActivity
          ? {
              id: license.TypeActivity.id,
              name: license.TypeActivity.Title,
            }
          : null,
        documentType: license.DocumentType
          ? {
              id: license.DocumentType.id,
              name: license.DocumentType.Title,
            }
          : null,
        nameDocument: license.NameDocument || '',
        documentNumber: license.DocumentNumber || null,
        startDateDocument: {
          startDate: license.StartDateDocument ? new Date(license.StartDateDocument) : null,
          endDate: license.StartDateDocument ? new Date(license.StartDateDocument) : null,
        },
        expirationDateDocument: {
          startDate: license.ExpirationDateDocument ? new Date(license.ExpirationDateDocument) : null,
          endDate: license.ExpirationDateDocument ? new Date(license.ExpirationDateDocument) : null,
        },
        validityPeriodDocument: license.ValidityPeriodDocument === 'Бессрочный' ? 'Бессрочный' : Number(license.ValidityPeriodDocument),
        detailedDescription: license.DetailedDescription || '',
        governmentAgency: license.GovernmentAgency
          ? {
              id: license.GovernmentAgency.id,
              name: license.GovernmentAgency.Title,
            }
          : null,
        namesStateBody: license.NamesStateBody || '',
        IsArchived: license.IsArchived,
        responsible: {
          id: license.Responsible?.id,
          email: license.ResponsibleEmail || license.Responsible?.Email,
          title: license.ResponsibleEmail ? users[license.ResponsibleEmail]?.displayName || license.Responsible?.Name : users[license.Responsible?.Email]?.displayName || license.Responsible?.Name,
          avatar: license.ResponsibleEmail ? users[license.ResponsibleEmail]?.avatar : users[license.Responsible?.Email]?.avatar,
          shortName: license.ResponsibleEmail ? users[license.ResponsibleEmail]?.shortName : users[license.Responsible?.Email]?.shortName,
        },
        interestedParties: (license.InterestedPartiesEmails || license.InterestedParties || []).map((emailOrUser: any) => {
          const email = typeof emailOrUser === 'string' ? emailOrUser : (emailOrUser.Email || emailOrUser.Name);
          return {
            id: typeof emailOrUser === 'string' ? null : emailOrUser.id,
            title: users[email]?.displayName || email,
            email: email,
            avatar: users[email]?.avatar,
            shortName: users[email]?.shortName,
          };
        }),
        actionStatus,
        comment: license.Comment1 || '',
        actions: formattedActions,
        // dateUpdate: new Date(license.DateUpdate),
        dateUpdate: {
          startDate: license.DateUpdate ? new Date(license.DateUpdate) : null,
          endDate: license.DateUpdate ? new Date(license.DateUpdate) : null,
        },
        periodicNotificationUpdateDate:
          license.PeriodicNotificationUpdateDate === null || license.PeriodicNotificationUpdateDate === 'нет'
            ? {
                id: 1,
                name: 'Нет',
              }
            : {
                id: 2,
                name: 'Да',
              },
        frequencyRepeatedNotifications: license.FrequencyRepeatedNotifications === null ? 0 : license.FrequencyRepeatedNotifications,
        products: license.Products || '',
        ND: license.ND || '',
        code_TN_VED: license.Code_TN_VED || '',
        code_OKPD: license.Code_OKPD || '',
        codeTypeAlcoholicBeverages: license.CodeTypeAlcoholicBeverages || '',
        certificationBody: license.CertificationBody || '',
        GTD_samples: license.GTD_samples || '',
        transferDeclarationBroker: license.TransferDeclarationBroker || '',
      };
    })
    .filter((license: any) => license !== null);
};
